/**
 * REC: what your hand (or the Director) does, turned into a use case that
 * can be played again, by you or anyone you send the file to. Recorded values
 * that would collide on a second run become variables: World names (so a
 * replay can use fresh ones, or the same ones, ensured), relay names (fresh
 * per run), days (a fresh far-future day, keeping their distance), soft-hold
 * deadlines (kept as "so long from now"), and ask tokens and ids (saved from
 * the answer that first gave them). An occupier's number is saved only from
 * where an answer gives an occupier's id, never from a seq that happens to
 * match; failing that it becomes the occupier's name. Keys never pass through
 * here: calls carry them in headers, and answers' secrets are never saved. A
 * check of the user's own Anthropic key is never a step: it exists to carry
 * that key, which no recording keeps.
 */

import type { CallRecord, CreateRecorder, Json, Persona, Step, UseCase, Unsub } from '../contracts.ts';
import { pathTo } from './format.ts';
import { objectOf } from '../model/json.ts';

/** Calls that make no sense to replay: one-shot tickets, sockets, the spec itself, sign-up, the operator, an Anthropic key's check. */
const SKIP = new Set(['watchTicket', 'watch', 'openapi', 'docs', 'mcp', 'signupConfig', 'signup', 'signupVerify', 'operatorIssueKey', 'assistKeyCheck']);
/** Answers that are lists: a value found in one sits at an index that may move, so they are the last place to save from. */
const LISTS = new Set(['listWorlds', 'listOccupiers', 'listHands', 'listRelays', 'listAsks', 'listGrants', 'listKeys', 'listWebhooks', 'journal', 'planes']);
/** Fields that name a World (bare, or `workspace/world`). */
const WORLD_FIELDS = new Set(['w', 'hand', 'world']);
const TOKEN = /^[0-9a-f]{32}$/;
const ID = /^(?:gr|key|wh)_[A-Za-z0-9]+$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const OCCUPIER_FIELDS = new Set(['who', 'from', 'to', 'holder']);

const dayMs = (d: string): number => Date.parse(`${d}T00:00:00Z`);

/** Turn recorded calls into a use case: the recorder's, and the Director's "save as use case" (stage/director.ts). */
export const toUseCase = (records: readonly CallRecord[], title: string, now: number = Date.now()): UseCase => {
  const kept = records.filter((r) => r.answer && r.answer.status !== 0 && !SKIP.has(r.op));
  const personas = [...new Set(kept.map((r) => r.persona))].sort() as Persona[];

  // -- whose workspace is whose, from answers that say so -------------------------------------
  const workspaces = new Map<Persona, string>();
  for (const r of kept) {
    const body = objectOf(r.answer!.body);
    if (typeof body.globalName === 'string' && (r.op === 'createWorld' || r.op === 'getWorld')) workspaces.set(r.persona, body.globalName.split('/')[0]!);
    if (typeof body.workspace === 'string' && (r.op === 'whoami' || r.op === 'usage' || r.op === 'billing')) workspaces.set(r.persona, body.workspace);
  }
  const personaOf = (ws: string): Persona | undefined => [...workspaces].find(([, w]) => w === ws)?.[0];

  // -- Worlds and relays become variables --------------------------------------------------------
  const worldVars = new Map<string, string>();   // `${persona}:${name}` → var
  const vars: Record<string, string> = {};
  const worldVar = (persona: Persona, name: string): string => {
    const key = `${persona}:${name}`;
    let v = worldVars.get(key);
    if (!v) {
      v = `w${worldVars.size + 1}`;
      worldVars.set(key, v);
      vars[v] = name;
    }
    return v;
  };
  for (const r of kept) {
    if (typeof r.args.w === 'string') worldVar(r.persona, r.args.w);
    const name = objectOf(r.args.body).name;
    if (r.op === 'createWorld' && typeof name === 'string') worldVar(r.persona, name);
  }
  const relayVars = new Map<string, string>();   // `${persona}:${w}:${relay}` → var
  for (const r of kept) {
    const name = objectOf(r.args.body).name;
    if (r.op === 'layRelay' && typeof name === 'string' && typeof r.args.w === 'string') {
      const v = `r${relayVars.size + 1}`;
      relayVars.set(`${r.persona}:${r.args.w}:${name}`, v);
      // A relay name is taken for good on its planner: each replay needs its own.
      vars[v] = `${name.slice(0, 56)}-{{run}}`;
    }
  }

  // -- days keep their distance from the first one --------------------------------------------------
  const days: string[] = [];
  const collectDays = (v: Json, key?: string): void => {
    if (typeof v === 'string' && key === 'date' && DAY.test(v)) days.push(v);
    else if (Array.isArray(v)) v.forEach((x) => collectDays(x));
    else if (v && typeof v === 'object') for (const [k, x] of Object.entries(v)) collectDays(x, k);
  };
  kept.forEach((r) => collectDays(r.args as Json));
  const first = days.length > 0 ? days.reduce((a, b) => (dayMs(a) <= dayMs(b) ? a : b)) : null;
  const dayVar = (d: string): string => {
    const n = Math.round((dayMs(d) - dayMs(first!)) / 86_400_000);
    return n === 0 ? '{{day}}' : `{{day${n > 0 ? '+' : ''}${n}}}`;
  };

  /** A World named in a string, as variables: 'mill', 'globex/mill'. */
  const worldString = (persona: Persona, s: string): string => {
    const own = worldVars.get(`${persona}:${s}`);
    if (own && !s.includes('/')) return `{{${own}}}`;
    const slash = s.indexOf('/');
    if (slash > 0) {
      const ws = s.slice(0, slash);
      const rest = s.slice(slash + 1);
      const p = personaOf(ws);
      if (p) {
        const v = worldVars.get(`${p}:${rest}`);
        return v ? `{{${p}}}/{{${v}}}` : `{{${p}}}/${rest}`;
      }
    }
    return s;
  };

  const steps: Step[] = [];
  const saves = new Map<number, Record<string, string>>();
  let savedCount = 0;
  const savedFor = new Map<string, string>();

  /** A value an earlier answer gave (a token, an id): saved there, named here. */
  const fromEarlier = (index: number, value: Json, prefix: string): string | null => {
    const known = savedFor.get(JSON.stringify(value));
    if (known) return `{{${known}}}`;
    const candidates = kept.slice(0, index).map((r, i) => ({ r, i })).reverse();
    const ordered = [...candidates.filter((c) => !LISTS.has(c.r.op)), ...candidates.filter((c) => LISTS.has(c.r.op))];
    for (const { r, i } of ordered) {
      const path = pathTo(r.answer!.body, value);
      if (!path || /secret/i.test(path)) continue;
      savedCount += 1;
      const name = `${prefix}${savedCount}`;
      saves.set(i, { ...(saves.get(i) ?? {}), [name]: path });
      savedFor.set(JSON.stringify(value), name);
      return `{{${name}}}`;
    }
    return null;
  };

  /** Occupiers of one World as earlier answers named them: id → name. */
  const occupiersSeen = (index: number, persona: Persona, w: string | null): Map<number, string> => {
    const out = new Map<number, string>();
    for (const e of kept.slice(0, index)) {
      if (e.persona !== persona || (e.args.w ?? null) !== w) continue;
      const body = objectOf(e.answer!.body);
      const one = objectOf(body.occupier);
      if (typeof one.id === 'number' && typeof one.name === 'string') out.set(one.id, one.name);
      if (Array.isArray(body.occupiers)) {
        for (const o of body.occupiers.map(objectOf)) if (typeof o.id === 'number' && typeof o.name === 'string') out.set(o.id, o.name);
      }
    }
    return out;
  };

  /**
   * An occupier named by number: saved from the answer that gave it as an
   * occupier's id (a declare, an ensured one, a holdings read, a take-in),
   * else its name where no other occupier seen in that World shares it, else
   * its place in a listing, else the number as recorded.
   */
  const occupierRef = (index: number, r: CallRecord, id: number): Json => {
    const w = typeof r.args.w === 'string' ? r.args.w : null;
    const cacheKey = `occupier|${r.persona}|${w}|${id}`;
    const known = savedFor.get(cacheKey);
    if (known) return `{{${known}}}`;
    const save = (i: number, path: string): string => {
      savedCount += 1;
      const name = `o${savedCount}`;
      saves.set(i, { ...(saves.get(i) ?? {}), [name]: path });
      savedFor.set(cacheKey, name);
      return `{{${name}}}`;
    };
    const earlier = kept.slice(0, index).map((e, i) => ({ e, i }))
      .filter(({ e }) => e.persona === r.persona && (e.args.w ?? null) === w).reverse();
    for (const { e, i } of earlier) {
      const body = objectOf(e.answer!.body);
      if (body.occupier === id) return save(i, 'body.occupier');
      if (objectOf(body.occupier).id === id) return save(i, 'body.occupier.id');
    }
    const seen = occupiersSeen(index, r.persona, w);
    const name = seen.get(id);
    if (name !== undefined && [...seen.values()].filter((n) => n === name).length === 1) {
      return name.includes('/') ? worldString(r.persona, name) : name;
    }
    for (const { e, i } of earlier) {
      const list = objectOf(e.answer!.body).occupiers;
      const at = Array.isArray(list) ? list.findIndex((o) => objectOf(o).id === id) : -1;
      if (at >= 0) return save(i, `body.occupiers[${at}].id`);
    }
    return id;
  };

  const rewrite = (r: CallRecord, index: number): Record<string, Json> => {
    const walk = (v: Json, key: string | undefined, parent: string | undefined): Json => {
      if (typeof v === 'string') {
        if (key === 'date' && DAY.test(v) && first) return dayVar(v);
        if (TOKEN.test(v) || ID.test(v)) return fromEarlier(index, v, TOKEN.test(v) ? 't' : 'id') ?? v;
        if (key === 'r' || (r.op === 'layRelay' && key === 'name' && parent === 'body')) {
          const w = typeof r.args.w === 'string' ? r.args.w : '';
          const rv = relayVars.get(`${r.persona}:${w}:${v}`);
          if (rv) return `{{${rv}}}`;
        }
        if (key !== undefined && (WORLD_FIELDS.has(key) || (r.op === 'createWorld' && key === 'name') || (r.op === 'relayAct' && key === 'to'))) {
          return worldString(r.persona, v);
        }
        if (key === 'to' && r.op === 'giveGrant') {
          const p = personaOf(v);
          if (p) return `{{${p}}}`;
          return worldString(r.persona, v);
        }
        if (key !== undefined && OCCUPIER_FIELDS.has(key) && v.includes('/')) return worldString(r.persona, v);
        return v;
      }
      if (typeof v === 'number') {
        if (key === 'until') return `{{now+${Math.max(0, Math.round(v - r.sentAt))}}}`;
        if (key !== undefined && OCCUPIER_FIELDS.has(key)) return occupierRef(index, r, v);
        return v;
      }
      if (Array.isArray(v)) return v.map((x) => walk(x, undefined, key));
      if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, walk(x, k, key)]));
      return v;
    };
    // The Idempotency-Key the Client gave this call belongs to it: replayed, it would name a different
    // body (a fresh day) and be refused, so every replayed call gets its own again.
    const { 'Idempotency-Key': _key, ...args } = r.args as Record<string, Json>;
    return walk(args, undefined, undefined) as Record<string, Json>;
  };

  kept.forEach((r, index) => {
    const status = r.answer!.status;
    const ensure = r.op === 'createWorld' || r.op === 'declareOccupier';
    // Replayed onto Worlds that exist, "it was already there" is the success ensure makes of it.
    const expected = ensure && (status === 409 || status === 400 || status === 201) ? 201 : status;
    const step: Step = { op: r.op, args: rewrite(r, index), expect: { status: expected } };
    if (r.persona !== 'A') step.as = r.persona;
    if (ensure) step.ensure = true;
    steps.push(step);
  });
  for (const [i, save] of saves) steps[i]!.save = save;

  const worlds = [...new Set([...worldVars.keys()].map((k) => k.slice(2)))];
  const stamp = new Date(now).toISOString();
  const director = kept.length > 0 && kept.every((r) => r.source.kind === 'director');
  return {
    v: 1,
    id: `rec-${now.toString(36)}`,
    title: title.trim().slice(0, 120) || 'My recording',
    blurb: `${steps.length} step${steps.length === 1 ? '' : 's'} recorded ${stamp.slice(0, 10)} at ${stamp.slice(11, 16)} UTC.`,
    origin: director ? 'director' : 'recorded',
    personas: personas.length > 0 ? personas : ['A'],
    worlds,
    ...(Object.keys(vars).length > 0 ? { vars } : {}),
    steps: steps.length > 0 ? steps : [{ op: 'health', args: {}, expect: { status: 200 } }],
  };
};

export const createRecorder: CreateRecorder = (client) => {
  let records: CallRecord[] = [];
  let unsub: Unsub | null = null;
  return {
    start() {
      unsub?.();
      records = [];
      unsub = client.on((record, phase) => {
        if (phase !== 'answered') return;
        if (record.source.kind !== 'hand' && record.source.kind !== 'director') return;
        records.push(record);
      });
    },
    stop(title) {
      unsub?.();
      unsub = null;
      const uc = toUseCase(records, title);
      records = [];
      return uc;
    },
    recording: () => unsub !== null,
  };
};
